"use client";

import { useMemo } from "react";
import { parseEther, zeroAddress } from "viem";
import { useAccount, useReadContract, useReadContracts } from "wagmi";
import { useAuth } from "@/context/auth";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import ContractAddress from "@/contract/addresses";
import { IS_GATEWAY_V2 } from "@/contract/gateway-version";
import { useDefaultParentCapacity } from "./useDefaultParentCapacity";
import { useMinimumPurchaseBv } from "./useMinimumPurchaseBv";
import {
  PARENT_MESSAGE_KEY,
  type ParentFieldStatus,
  useParentFieldValidation,
} from "./useParentFieldValidation";
import {
  REFERRER_MESSAGE_KEY,
  type ReferrerFieldStatus,
  useReferrerFieldValidation,
} from "./useReferrerFieldValidation";

/**
 * Which of the two placement fields the buyer needs to deal with right now.
 *
 * - `loading`         — still reading chain state; decide nothing yet.
 * - `unreachable`     — the chain read failed (RPC blip, node restart). Kept
 *                        distinct from `loading` so the UI can say so and
 *                        offer a retry, instead of showing a spinner forever
 *                        on a request that has already given up.
 * - `no-wallet`       — no wallet connected.
 * - `wallet-mismatch` — the connected wallet is not the signed-in account.
 * - `placed`          — buyer is already in the genealogy tree. Both fields
 *                        are permanently irrelevant; the contract ignores them.
 * - `permanent-guest` — V2 only. Buyer exists but their locked referrer is the
 *                        zero address (elected on their first purchase). They
 *                        can never be placed, on this purchase or any future
 *                        one — nothing to show or collect.
 * - `floating`        — V2 only. Buyer exists, has a real locked referrer,
 *                        but has not accumulated enough BV to be placed yet.
 *                        Referrer is fixed (read-only); parent is still up
 *                        for grabs every purchase until placement happens.
 * - `new`             — first-ever purchase. On V2 this decides the referrer
 *                        forever (blank = permanent guest, see above); on V1
 *                        there is no separate referrer concept, so only the
 *                        parent field is shown and it is required.
 */
export type PlacementMode =
  | "loading"
  | "unreachable"
  | "no-wallet"
  | "wallet-mismatch"
  | "placed"
  | "permanent-guest"
  | "floating"
  | "new";

export interface UsePlacementResolutionReturn {
  mode: PlacementMode;
  /** V2 only: show a separate, editable referrer field (mode === "new"). */
  showReferrerField: boolean;
  /** Show an editable parent field at all. */
  showParentField: boolean;
  /** Referrer field status. Idle/valid on V1, where there is no such field. */
  referrerStatus: ReferrerFieldStatus;
  /** The locked on-chain referrer, shown read-only in "floating" mode. */
  lockedReferrerAddress: string | undefined;
  parentStatus: ParentFieldStatus;
  /**
   * True when the parent field is blank (relying on the default-to-referrer
   * fallback) AND that referrer's tree is confirmed full on-chain. The
   * contract would revert `ParentCapacityReached` on submit; this lets the UI
   * warn before the buyer wastes gas on it.
   */
  defaultParentFull: boolean;
  /** Address to submit as the placement's `referrer` argument. */
  referrerAddress: string | undefined;
  /** Address to submit as the placement's `parent` argument. */
  parentAddress: string | undefined;
  /**
   * True when THIS purchase is the one that permanently fixes the buyer's
   * tree position (or, in "new" mode with a blank referrer, permanently
   * excludes them from ever having one). Drives the irreversible-decision
   * confirmation.
   */
  willLockPlacement: boolean;
  /** The wallet this purchase will be signed by (wagmi's view). */
  buyerAddress: string | undefined;
  /** Gate for the final checkout button. */
  canPurchase: boolean;
  /** Key under the `checkout` namespace explaining why `canPurchase` is false. */
  blockedMessageKey: string | undefined;
  /** Manually re-attempt the account read after mode === "unreachable". */
  retry: () => void;
}

const ROOT_USER_ID = 1n;
const PLACEMENT_BV_THRESHOLD_LEVEL = 7n;

/**
 * Just the two functions needed here, rather than the full GATEWAY_ABI --
 * useReadContracts resolves its return type per entry, and the 148-entry V2
 * ABI trips TypeScript's "type instantiation is excessively deep" limit (see
 * useBvPlan.ts, which hit the same wall). `referralPercent` is V2-only;
 * `BVPlan` exists on both generations.
 */
const PLACEMENT_INPUTS_ABI = [
  {
    type: "function",
    name: "referralPercent",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256", internalType: "uint256" }],
  },
  {
    type: "function",
    name: "BVPlan",
    stateMutability: "view",
    inputs: [{ name: "", type: "uint256", internalType: "uint256" }],
    outputs: [{ name: "", type: "uint256", internalType: "uint256" }],
  },
] as const;

/**
 * Single source of truth for "who will this buyer be placed under, and by
 * whom were they referred?".
 *
 * Reads the CONNECTED wallet's on-chain state (never the JWT session wallet,
 * never a cached/previous value) and derives one of the {@link PlacementMode}
 * states from it directly. There is no client-side "guest mode" election or
 * house-account fallback any more: an empty referrer on a first purchase IS
 * the permanent-guest election, enforced by the contract itself
 * (`Users._requiresPlacement` needs a non-zero locked referrer to ever place
 * anyone). Nothing here can silently reuse a stale address — every render
 * re-derives from the current `useAccount()` address and the live chain read.
 *
 * `cartGrossBv` is today's cart total (in whole BV units, matching how the
 * rest of checkout already tracks it) — used only to warn the buyer BEFORE
 * they submit that this specific purchase will lock their tree position.
 *
 * `minItemGrossBv` is the smallest per-unit BV among the cart's line items
 * (defaults to `cartGrossBv`, correct only when there is exactly one unit).
 * It drives the below-minimum-purchase gate specifically: the gateway's
 * `minimumPurchaseBv` check runs PER ORDER ITEM, not on the cart total
 * (`MarketV2.buyNft` loops the item array and calls `paymentFrom` once per
 * unit — see `useMinimumPurchaseBv`), so a cart whose SUM clears the
 * minimum can still contain one below-minimum item that reverts every
 * `buyNft` call regardless of how much quantity is stacked around it.
 */
export const usePlacementResolution = (
  referrerInput: string | undefined,
  parentInput: string | undefined,
  cartGrossBv: number,
  minItemGrossBv: number = cartGrossBv,
): UsePlacementResolutionReturn => {
  const { address } = useAccount();
  const jwtWallet = useAuth((s) => s.user?.wallet_address);

  // Read placement for the CONNECTED wallet — the one that will be
  // `msg.sender` — never for the session identity.
  const buyer = useReadContract({
    address: ContractAddress.GATEWAY,
    abi: GATEWAY_ABI,
    functionName: "getUser",
    args: [address as `0x${string}`],
    query: {
      enabled: !!address,
      // A transient RPC blip (node restart, brief network hiccup) must not
      // strand the checkout gate forever: react-query's default retry gives
      // up after a few attempts and only refetches again on window focus or
      // a network online event, neither of which fires for "the local node
      // came back up while the tab stayed focused and the network adapter
      // never toggled". Keep polling every 5s specifically while this query
      // is erroring so it self-heals without the buyer having to know to
      // refresh the page.
      refetchInterval: (query) => (query.state.error ? 5000 : false),
    },
  });

  const chainInputs = useReadContracts({
    contracts: [
      {
        address: ContractAddress.GATEWAY,
        abi: PLACEMENT_INPUTS_ABI,
        functionName: "referralPercent",
      },
      {
        address: ContractAddress.GATEWAY,
        abi: PLACEMENT_INPUTS_ABI,
        functionName: "BVPlan",
        args: [PLACEMENT_BV_THRESHOLD_LEVEL],
      },
    ],
    query: { enabled: IS_GATEWAY_V2 },
  });

  const buyerData = buyer.data as
    | {
        exists: boolean;
        id: bigint;
        depth: bigint;
        referrer?: `0x${string}`;
        bv: { summ: bigint };
      }
    | undefined;

  const isInGenealogy = (user: {
    exists: boolean;
    id: bigint;
    depth: bigint;
  }) => user.exists && (user.id === ROOT_USER_ID || user.depth > 0n);

  const baseMode = useMemo<
    | "loading"
    | "unreachable"
    | "no-wallet"
    | "wallet-mismatch"
    | "placed"
    | "resolving-new"
  >(() => {
    if (!address) return "no-wallet";
    if (jwtWallet && address.toLowerCase() !== jwtWallet.toLowerCase()) {
      return "wallet-mismatch";
    }
    if (buyer.isLoading || (!buyerData && !buyer.isError)) return "loading";
    // An unreadable chain must not be treated as "new user" — that would ask
    // for a referrer we might then apply to an already-placed wallet. Kept
    // distinct from "loading" so the UI stops spinning and tells the truth;
    // the query itself keeps auto-retrying every 5s (see the buyer read).
    if (buyer.isError) return "unreachable";
    if (!buyerData?.exists) return "resolving-new";
    return isInGenealogy(buyerData) ? "placed" : "resolving-new";
  }, [address, jwtWallet, buyer.isLoading, buyer.isError, buyerData]);

  const mode = useMemo<PlacementMode>(() => {
    if (baseMode !== "resolving-new") return baseMode;
    if (!buyerData?.exists) return "new";
    if (!IS_GATEWAY_V2) return "new"; // V1 has no unplaced-but-registered state
    const lockedReferrer = buyerData.referrer ?? zeroAddress;
    return lockedReferrer.toLowerCase() === zeroAddress
      ? "permanent-guest"
      : "floating";
  }, [baseMode, buyerData]);

  const showReferrerField = IS_GATEWAY_V2 && mode === "new";
  const showParentField = mode === "new" || mode === "floating";

  const referrer = useReferrerFieldValidation(
    showReferrerField ? referrerInput : "",
  );
  const parent = useParentFieldValidation(showParentField ? parentInput : "");

  const lockedReferrerAddress =
    mode === "floating" ? buyerData?.referrer : undefined;

  // V1 and "already placed"/"permanent guest": no field to submit, or the
  // contract ignores whatever is sent — pass the buyer's own address so the
  // argument stays well-formed and non-zero, matching the previous behaviour.
  let referrerAddress: string | undefined;
  let parentAddress: string | undefined;

  // Parent field: blank is a legitimate submission whenever the field is
  // shown at all — Users._resolvePlacementParent treats a zero parent as
  // "use the locked referrer". Only a NON-blank value has to pass live
  // validation before it can be sent.
  const parentTyped = (parentInput ?? "").trim();
  const parentBlank = parentTyped.length === 0;

  // A blank parent silently becomes the referrer (mode "new") or the locked
  // referrer (mode "floating") on-chain. If THAT address's tree is already
  // full, the contract reverts ParentCapacityReached — check for it here so
  // the buyer gets a warning instead of a failed transaction. Only enabled
  // once the referrer itself is a confirmed, valid address, so this never
  // fires against a still-typing or invalid referrer.
  const defaultParentCandidate =
    mode === "floating"
      ? lockedReferrerAddress
      : mode === "new" && IS_GATEWAY_V2
        ? (referrerInput ?? "").trim()
        : undefined;
  const defaultParentCheckEnabled =
    parentBlank &&
    !!defaultParentCandidate &&
    (mode === "floating" || (mode === "new" && referrer.isValid));
  const defaultParentCapacity = useDefaultParentCapacity(
    defaultParentCandidate,
    defaultParentCheckEnabled,
  );
  const defaultParentFull = parentBlank && defaultParentCapacity.isFull;

  const parentOkToSubmit = parentBlank ? !defaultParentFull : parent.isValid;
  const parentOrDefault = parentBlank ? zeroAddress : parentTyped;

  if (mode === "placed" || mode === "permanent-guest") {
    referrerAddress = address;
    parentAddress = address;
  } else if (mode === "floating") {
    referrerAddress = lockedReferrerAddress;
    parentAddress = parentOkToSubmit ? parentOrDefault : undefined;
  } else if (mode === "new") {
    if (!IS_GATEWAY_V2) {
      // V1: one field only, and it is required (no zero-address guest path —
      // the DAO auto-placement fallback this used to fall back to is retired).
      referrerAddress = parent.isValid ? parentInput?.trim() : undefined;
      parentAddress = referrerAddress;
    } else {
      const referrerTyped = (referrerInput ?? "").trim();
      referrerAddress =
        referrerTyped.length === 0
          ? zeroAddress
          : referrer.isValid
            ? referrerTyped
            : undefined;
      // Parent only matters if a referrer was actually given — otherwise the
      // contract never attempts placement, so do not ask for or send one.
      if (referrerAddress) {
        parentAddress = parentOkToSubmit ? parentOrDefault : undefined;
      }
    }
  }

  // Will THIS purchase permanently fix the buyer's placement?
  const willLockPlacement = useMemo(() => {
    if (mode === "new") {
      if (!IS_GATEWAY_V2) return true; // V1 places immediately, always
      const referrerTyped = (referrerInput ?? "").trim();
      if (referrerTyped.length === 0) return true; // permanent-guest election
      // A referrer was given: this purchase locks placement only if it alone
      // reaches the 10bv threshold (existing balance is zero for a new user).
      return crossesPlacementThreshold(0n, cartGrossBv, chainInputs.data);
    }
    if (mode === "floating") {
      const existingSumm = buyerData?.bv.summ ?? 0n;
      return crossesPlacementThreshold(
        existingSumm,
        cartGrossBv,
        chainInputs.data,
      );
    }
    return false;
  }, [mode, referrerInput, cartGrossBv, chainInputs.data, buyerData]);

  // The gateway rejects any purchase under this outright, whoever the buyer
  // is — it is checked before the new-user branch, so it applies to every
  // mode below including `placed` and `permanent-guest`. Catching it here
  // turns a post-signature revert into a message shown before they pay.
  //
  // Checked against minItemGrossBv (the smallest per-unit item), not the
  // cart total: the gate runs PER ORDER ITEM on chain, so summing across
  // items or quantity would pass a cart that still reverts on submit.
  //
  // Compared in wei rather than whole units: minItemGrossBv is a float, and
  // 0.1 + 0.2 style drift at the boundary would reject a cart the contract
  // would actually have accepted.
  const minimumPurchaseBvWei = useMinimumPurchaseBv();
  const belowMinimumPurchaseBv =
    minimumPurchaseBvWei > 0n &&
    minItemGrossBv > 0 &&
    parseEther(String(minItemGrossBv)) < minimumPurchaseBvWei;

  const canPurchase =
    !belowMinimumPurchaseBv &&
    (mode === "placed" ||
      mode === "permanent-guest" ||
      (mode === "floating" && parentOkToSubmit) ||
      (mode === "new" &&
        (!IS_GATEWAY_V2
          ? parent.isValid
          : referrerAddress === zeroAddress ||
            (referrer.isValid && parentOkToSubmit))));

  const blockedMessageKey = canPurchase
    ? undefined
    : belowMinimumPurchaseBv
      ? "referrer.belowMinimumPurchaseBv"
      : mode === "wallet-mismatch"
        ? "referrer.walletMismatch"
        : mode === "no-wallet"
          ? "referrer.connectWallet"
          : mode === "loading"
            ? "referrer.verifying"
            : mode === "unreachable"
              ? "referrer.accountUnreachable"
              : defaultParentFull
                ? "referrer.defaultParentFull"
                : mode === "floating" || (mode === "new" && !IS_GATEWAY_V2)
                  ? `referrer.${PARENT_MESSAGE_KEY[parent.status]}`
                  : // mode === "new" && IS_GATEWAY_V2: report whichever field is
                    // actually the problem — referrer first (it is the one that
                    // can never be fixed later), then parent.
                    !referrer.isValid && referrerAddress !== zeroAddress
                    ? `referrer.${REFERRER_MESSAGE_KEY[referrer.status]}`
                    : `referrer.${PARENT_MESSAGE_KEY[parent.status]}`;

  return {
    mode,
    showReferrerField,
    showParentField,
    referrerStatus: referrer.status,
    lockedReferrerAddress,
    parentStatus: parent.status,
    defaultParentFull,
    referrerAddress,
    parentAddress,
    willLockPlacement,
    buyerAddress: address,
    canPurchase,
    blockedMessageKey,
    retry: buyer.refetch,
  };
};

/**
 * Mirrors Gateway._paymentProcess's placement gate:
 *   grossBv -> referralCut = floor(grossBv * percent / 1 ether)
 *   effectiveBv = grossBv - referralCut
 *   places when floor(existingSumm + effectiveBv) reaches the level-7 (10bv)
 *   threshold, read live from BVPlan(7) rather than hardcoded.
 */
function crossesPlacementThreshold(
  existingSummWei: bigint,
  cartGrossBv: number,
  chainInputs: readonly { result?: unknown; status: string }[] | undefined,
): boolean {
  const percentResult = chainInputs?.[0];
  const thresholdResult = chainInputs?.[1];
  if (
    !percentResult ||
    percentResult.status !== "success" ||
    !thresholdResult ||
    thresholdResult.status !== "success"
  ) {
    return false; // Chain reads not ready — never warn on an unverified guess.
  }

  const percent = percentResult.result as bigint;
  const threshold = thresholdResult.result as bigint;
  const grossBvWei = parseEther(`${cartGrossBv}`);
  const referralCut = (grossBvWei * percent) / parseEther("1");
  const effectiveBv = grossBvWei - referralCut;

  return existingSummWei + effectiveBv >= threshold;
}
